import { useState, useRef, useCallback } from 'react';

interface Project {
  id: number;
  title: string;
  category: string;
  location: string;
  description: string;
  beforeSrc: string;
  afterSrc: string;
}

const PROJECTS: Project[] = [
  {
    id: 1,
    title: 'Full Kitchen Gut Renovation',
    category: 'Interior Remodel',
    location: 'Lincoln Park, Chicago',
    description: 'Complete tear-down of a 1970s galley kitchen — rebuilt with custom shaker cabinets, quartz countertops, and all-new plumbing.',
    beforeSrc: 'https://public.readdy.ai/ai/img_res/6d3690b87619db624d935fe669e011ae.jpg',
    afterSrc: 'https://readdy.ai/api/search-image?query=beautifully%20renovated%20modern%20Chicago%20kitchen%20bright%20white%20shaker%20cabinets%20polished%20quartz%20countertops%20stainless%20steel%20appliances%20pendant%20lighting%20open%20airy%20clean%20contemporary%20after%20remodel&width=700&height=420&seq=ba_kitchen_after_01&orientation=landscape',
  },
  {
    id: 2,
    title: 'Master Bathroom Remodel',
    category: 'Bathroom Renovation',
    location: 'Wicker Park, Chicago',
    description: 'Outdated 90s bathroom fully transformed — new large-format tiling, walk-in shower, floating vanity, and all fixtures replaced.',
    beforeSrc: 'https://readdy.ai/api/search-image?query=old%20cramped%20outdated%201990s%20Chicago%20bathroom%20cracked%20pink%20ceramic%20tiles%20stained%20tub%20worn%20vanity%20dingy%20dim%20lighting%20residential%20rundown%20before%20renovation&width=700&height=420&seq=ba_bath_before_01&orientation=landscape',
    afterSrc: 'https://readdy.ai/api/search-image?query=modern%20renovated%20Chicago%20master%20bathroom%20large%20white%20wall%20tiles%20frameless%20glass%20walk-in%20shower%20floating%20vanity%20matte%20black%20fixtures%20bright%20spa-like%20clean%20contemporary%20after%20remodel&width=700&height=420&seq=ba_bath_after_01&orientation=landscape',
  },
  {
    id: 3,
    title: 'Exterior Brick Tuckpointing',
    category: 'Masonry & Exterior',
    location: 'Hyde Park, Chicago',
    description: 'Decades of Chicago winters had eroded the mortar joints on this classic greystone. Full tuckpointing restored structural integrity and curb appeal.',
    beforeSrc: 'https://readdy.ai/api/search-image?query=deteriorated%20crumbling%20old%20Chicago%20greystone%20brick%20facade%20home%20damaged%20eroded%20mortar%20joints%20weatherbeaten%20brickwork%20exterior%20before%20masonry%20restoration&width=700&height=420&seq=ba_brick_before_01&orientation=landscape',
    afterSrc: 'https://readdy.ai/api/search-image?query=freshly%20restored%20tuckpointed%20red%20brick%20facade%20classic%20Chicago%20residential%20greystone%20home%20crisp%20clean%20new%20mortar%20joints%20perfect%20masonry%20professional%20exterior%20after%20restoration&width=700&height=420&seq=ba_brick_after_01&orientation=landscape',
  },
];

interface SliderProps {
  beforeSrc: string;
  afterSrc: string;
  title: string;
}

function BeforeAfterSlider({ beforeSrc, afterSrc, title }: SliderProps) {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const updateSlider = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSliderPos((x / rect.width) * 100);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    updateSlider(e.clientX);
  }, [updateSlider]);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging.current) updateSlider(e.clientX);
  }, [updateSlider]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    updateSlider(e.touches[0].clientX);
  }, [updateSlider]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    updateSlider(e.touches[0].clientX);
  }, [updateSlider]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-72 overflow-hidden rounded-xl select-none cursor-col-resize"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      {/* Before image — base layer */}
      <img
        src={beforeSrc}
        alt={`Before – ${title}`}
        className="absolute inset-0 w-full h-full object-cover object-top"
        draggable={false}
      />

      {/* After image — clipped to right side of slider */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 0 0 ${sliderPos}%)` }}
      >
        <img
          src={afterSrc}
          alt={`After – ${title}`}
          className="w-full h-full object-cover object-top"
          draggable={false}
        />
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white/90 pointer-events-none"
        style={{ left: `${sliderPos}%` }}
      />

      {/* Drag handle */}
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 bg-white rounded-full flex items-center justify-center pointer-events-none"
        style={{ left: `${sliderPos}%` }}
      >
        <div className="w-9 h-9 flex items-center justify-center">
          <i className="ri-arrow-left-right-line text-gray-700 text-base" />
        </div>
      </div>

      {/* Labels */}
      <div
        className="absolute top-3 left-3 bg-black/55 text-white text-xs font-bold px-3 py-1.5 rounded-full tracking-wider pointer-events-none"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        BEFORE
      </div>
      <div
        className="absolute top-3 right-3 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full tracking-wider pointer-events-none"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        AFTER
      </div>
    </div>
  );
}

export default function BeforeAfterSection() {
  return (
    <section id="projects" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <div
            className="text-red-600 text-sm font-semibold tracking-widest uppercase mb-3"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Our Work
          </div>
          <h2
            className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-5"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            BEFORE &amp; AFTER
            <br />
            <span className="text-red-700">REAL RESULTS.</span>
          </h2>
          <p
            className="text-gray-500 text-base max-w-lg mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Drag the handle to reveal the transformation. Every project is a Chicago home brought back to life.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {PROJECTS.map((project) => (
            <div key={project.id} className="flex flex-col gap-4">
              <BeforeAfterSlider
                beforeSrc={project.beforeSrc}
                afterSrc={project.afterSrc}
                title={project.title}
              />
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className="bg-red-50 text-red-700 text-xs font-semibold px-3 py-1 rounded-full"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {project.category}
                  </span>
                  <span
                    className="text-gray-400 text-xs flex items-center gap-1"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <i className="ri-map-pin-2-line text-xs" />
                    {project.location}
                  </span>
                </div>
                <h3
                  className="text-xl font-black text-gray-900 mb-1"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  {project.title}
                </h3>
                <p
                  className="text-gray-500 text-sm leading-relaxed"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {project.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p
            className="text-gray-700 text-sm mb-5"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Your Legacy Starts Today
          </p>
          <a
            href="#contact"
            className="inline-block bg-red-700 text-white text-sm font-bold px-9 py-4 rounded-full hover:bg-red-800 transition-colors whitespace-nowrap"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Start Your Project
          </a>
        </div>
      </div>
    </section>
  );
}
